import { useState } from 'react';

interface WebPImageProps {
  src: string;
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  onLoad?: () => void;
  onError?: () => void;
  width?: string | number;
  height?: string | number;
  srcSet?: string;
  sizes?: string;
  style?: React.CSSProperties;
  decoding?: 'async' | 'sync' | 'auto';
  draggable?: boolean;
  fetchpriority?: 'high' | 'low' | 'auto';
}

const WebPImage = ({ 
  src, 
  alt, 
  className,
  loading = 'lazy',
  onLoad,
  onError,
  width,
  height,
  srcSet,
  sizes,
  style,
  decoding = 'async',
  draggable,
  fetchpriority
}: WebPImageProps) => {
  const [useWebP, setUseWebP] = useState(true);
  
  // Generate WebP source URL
  const getWebPSrc = (originalSrc: string) => {
    if (!originalSrc) return originalSrc;
    return originalSrc.replace(/\.(png|jpg|jpeg)$/i, '.webp');
  };
  
  // Generate responsive srcSet with proper sizing
  const generateSrcSet = (originalSrc: string, format: 'webp' | 'original' = 'webp') => {
    if (!originalSrc) return '';
    const baseUrl = format === 'webp' ? getWebPSrc(originalSrc) : originalSrc;
    
    // For gallery images, create proper responsive sizes
    if (srcSet) {
      return format === 'webp' ? srcSet.replace(/\.(png|jpg|jpeg)/gi, '.webp') : srcSet;
    }
    
    return baseUrl;
  };

  const handleError = () => {
    if (useWebP) {
      // Fallback to PNG if WebP fails
      setUseWebP(false);
    }
    onError?.();
  };

  const finalSrc = useWebP ? getWebPSrc(src) : src;
  const finalSrcSet = generateSrcSet(src, useWebP ? 'webp' : 'original');
  
  return (
    <picture>
      {/* Try WebP first for supported browsers */}
      {useWebP && (
        <source 
          type="image/webp" 
          srcSet={finalSrcSet}
          sizes={sizes}
        />
      )}
      
      {/* PNG/JPG fallback */}
      <source 
        type={src.endsWith('.png') ? 'image/png' : 'image/jpeg'}
        srcSet={srcSet || src}
        sizes={sizes}
      />
      
      {/* Final fallback img */}
      <img
        src={finalSrc}
        alt={alt}
        className={className}
        loading={loading}
        decoding={decoding}
        onLoad={onLoad}
        onError={handleError}
        width={width}
        height={height}
        sizes={sizes}
        style={style}
        draggable={draggable}
        {...(fetchpriority ? { fetchpriority } : {})}
      />
    </picture>
  );
};

export default WebPImage;
